import React, { createContext, useState, useEffect, useContext } from "react";
import { useNavigate } from "react-router-dom";

export const AdminContext = createContext();

const API_BASE_URL = process.env.REACT_APP_API_URL || ""; // Ajustez selon votre configuration

export const AdminProvider = ({ children }) => {
  const navigate = useNavigate();
  const [authToken, setAuthToken] = useState(null);
  const [admin, setAdmin] = useState(null);
  const [permissions, setPermissions] = useState({ allowedRoutes: [] });
  const [loading, setLoading] = useState(true);
  const [isInitialized, setIsInitialized] = useState(false);

  // Fonction pour faire les requêtes API
  const apiRequest = async (endpoint, options = {}) => {
    const url = `${API_BASE_URL}/api${endpoint}`;
    const config = {
      headers: {
        "Content-Type": "application/json",
        ...(authToken && { Authorization: `Bearer ${authToken}` }),
      },
      ...options,
    };

    const response = await fetch(url, config);

    if (!response.ok) {
      if (response.status === 401) {
        // Token expiré ou invalide
        logout();
        throw new Error("Non autorisé");
      }
      throw new Error(`Erreur API: ${response.status}`);
    }

    return response.json();
  };

  // Fonction pour récupérer les informations de l'admin connecté
  const fetchAdminData = async (token) => {
    try {
      const data = await apiRequest("/auth/admin/me", {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      setAdmin(data.admin);
      setPermissions(data.permissions);
      return true;
    } catch (error) {
      console.error("Erreur lors de la récupération des données admin:", error);
      return false;
    }
  };

  // Initialisation au chargement
  useEffect(() => {
    const initializeAuth = async () => {
      const tokenFromStorage =
        localStorage.getItem("authToken") ||
        document.cookie
          .split("; ")
          .find((row) => row.startsWith("userAuth="))
          ?.split("=")[1];

      if (tokenFromStorage) {
        setAuthToken(tokenFromStorage);
        const success = await fetchAdminData(tokenFromStorage);
        if (!success) {
          // Token invalide, nettoyer et rediriger
          logout();
        }
      } else {
        // Pas de token, rediriger vers sign-in
        navigate("/sign-in");
      }

      setLoading(false);
      setIsInitialized(true);
    };

    if (!isInitialized) {
      initializeAuth();
    }
  }, [navigate, isInitialized]);

  // Fonction de connexion
  const login = async (uid, password) => {
    try {
      const data = await apiRequest("/auth/admin/login", {
        method: "POST",
        body: JSON.stringify({ uid, password }),
      });

      const token = data.token;

      // Stocker le token
      localStorage.setItem("authToken", token);
      document.cookie = `userAuth=${token}; path=/; max-age=${
        60 * 60 * 24 * 7
      }`;

      setAuthToken(token);
      setAdmin(data.admin);

      // Récupérer les permissions complètes
      await fetchAdminData(token);

      navigate("/dashboard");
      return { success: true, message: data.message };
    } catch (error) {
      console.error("Erreur de connexion:", error);
      return { success: false, message: error.message };
    }
  };

  // Fonction de déconnexion
  const logout = () => {
    localStorage.removeItem("authToken");
    document.cookie = "userAuth=; path=/; max-age=0";
    setAuthToken(null);
    setAdmin(null);
    setPermissions({ allowedRoutes: [] });
    navigate("/sign-in");
  };

  // Vérifier si l'admin a accès à une route
  const hasRouteAccess = (route) => {
    if (!permissions.allowedRoutes || permissions.allowedRoutes.length === 0) {
      return false;
    }

    // Vérification exacte
    if (permissions.allowedRoutes.includes(route)) {
      return true;
    }

    // Vérification pour les routes dynamiques (avec paramètres)
    return permissions.allowedRoutes.some((allowedRoute) => {
      // Convertir les routes avec paramètres en regex
      const routeRegex = allowedRoute.replace(/:[^/]+/g, "[^/]+");
      const regex = new RegExp(`^${routeRegex}$`);
      return regex.test(route);
    });
  };

  // Vérifier les actions disponibles sur une route
  const checkRouteActions = async (route) => {
    try {
      const data = await apiRequest("/auth/admin/check-access", {
        method: "POST",
        body: JSON.stringify({ route }),
      });

      return {
        route: data.route,
        actions: data.actions || [],
      };
    } catch (error) {
      console.error("Erreur lors de la vérification des actions:", error);
      return {
        route,
        actions: [],
      };
    }
  };

  // Vérifier si l'admin peut effectuer une action spécifique
  const canPerformAction = async (route, action) => {
    const routeActions = await checkRouteActions(route);
    return routeActions.actions.includes(action);
  };

  // Filtrer les routes pour la navigation
  const getAccessibleRoutes = () => {
    const allRoutes = [
      { path: "/dashboard", name: "Dashboard", icon: "dashboard" },
      { path: "/users", name: "Utilisateurs", icon: "user" },
      { path: "/organisations", name: "Organisations", icon: "bank" },
      { path: "/billing", name: "Facturation", icon: "dollar" },
      { path: "/transactions", name: "Transactions", icon: "transaction" },
      { path: "/airtime", name: "Airtime", icon: "mobile" },
      { path: "/supply", name: "Approvisionnement", icon: "supply" },
      { path: "/qr-code", name: "QR Code", icon: "qrcode" },
      { path: "/commercials", name: "Commerciaux", icon: "team" },
      { path: "/verification", name: "Vérifications", icon: "safety" },
      { path: "/admin-management", name: "Admin Management", icon: "admin" },
    ];

    return allRoutes.filter((route) => hasRouteAccess(route.path));
  };

  // Composant de route protégée
  const ProtectedRoute = ({
    children,
    requiredRoute,
    requiredAction = null,
  }) => {
    if (loading) {
      return <div>Chargement...</div>; // Ou votre composant Spin
    }

    if (!authToken) {
      navigate("/sign-in");
      return null;
    }

    if (!hasRouteAccess(requiredRoute)) {
      // Rediriger vers une page d'accès refusé ou dashboard
      navigate("/dashboard");
      return <div>Accès refusé à cette ressource.</div>;
    }

    // Si une action spécifique est requise, on peut faire une vérification supplémentaire
    // Cette vérification pourrait être asynchrone selon vos besoins

    return children;
  };

  // Hook personnalisé pour utiliser le contexte
  const useAdminPermissions = () => {
    const context = useContext(AdminContext);
    if (!context) {
      throw new Error(
        "useAdminPermissions doit être utilisé à l'intérieur d'AdminProvider",
      );
    }
    return context;
  };

  const contextValue = {
    // État
    authToken,
    admin,
    permissions,
    loading,
    isInitialized,

    // Fonctions d'authentification
    login,
    logout,

    // Fonctions de vérification des permissions
    hasRouteAccess,
    checkRouteActions,
    canPerformAction,
    getAccessibleRoutes,

    // Composant de route protégée
    ProtectedRoute,

    // Données dérivées
    isAuthenticated: !!authToken,
    adminRole: admin?.role,
    adminFullname: admin?.fullname,
  };

  return (
    <AdminContext.Provider value={contextValue}>
      {children}
    </AdminContext.Provider>
  );
};

// Hook personnalisé pour faciliter l'utilisation
export const useAdminPermissions = () => {
  const context = useContext(AdminContext);
  if (!context) {
    throw new Error(
      "useAdminPermissions doit être utilisé à l'intérieur d'AdminProvider",
    );
  }
  return context;
};

// Composant HOC pour protéger les routes
export const withAdminAccess = (
  WrappedComponent,
  requiredRoute,
  requiredAction = null,
) => {
  return function AdminProtectedComponent(props) {
    const { ProtectedRoute } = useAdminPermissions();

    return (
      <ProtectedRoute
        requiredRoute={requiredRoute}
        requiredAction={requiredAction}
      >
        <WrappedComponent {...props} />
      </ProtectedRoute>
    );
  };
};
